"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import clsx from "clsx";
import { useReducedMotion } from "framer-motion";
import { useLocale } from "@/lib/locale/locale-provider";
import { LanguageSwitcher } from "@/components/ui/language-switcher";
import { NavOverlay } from "./NavOverlay";
import {
  ensureHomeLink,
  filterAvailableHashLinks,
  normalizeNavItems,
  scrollToHref,
  type SiteNavItem,
} from "./navigation.utils";
import styles from "./navigation.module.css";

type NavbarProps = {
  brand: string;
  navItems: SiteNavItem[];
  cta?: { label: string; href: string };
  sticky?: boolean;
  transparent?: boolean;
};

export function Navbar({
  brand,
  navItems,
  cta,
  sticky = true,
  transparent = false,
}: NavbarProps) {
  const locale = useLocale();
  const prefersReducedMotion = useReducedMotion();
  const toggleRef = useRef<HTMLButtonElement>(null);
  const brandLabel = useMemo(() => brand.replace(/\s*&\s*/g, "&").trim(), [brand]);

  const copy = useMemo(() => {
    if (locale === "fr") {
      return {
        home: "Accueil",
        menu: "Menu",
        close: "Fermer",
        overlayEyebrow: "Navigation",
        overlayAria: "Navigation du site",
        navLabel: "Liens principaux",
        languageSwitcher: "Changer de langue",
      };
    }

    if (locale === "en") {
      return {
        home: "Home",
        menu: "Menu",
        close: "Close",
        overlayEyebrow: "Navigation",
        overlayAria: "Site navigation",
        navLabel: "Primary links",
        languageSwitcher: "Switch language",
      };
    }

    return {
      home: "Start",
      menu: "Menue",
      close: "Schliessen",
      overlayEyebrow: "Navigation",
      overlayAria: "Seitennavigation",
      navLabel: "Hauptnavigation",
      languageSwitcher: "Sprache wechseln",
    };
  }, [locale]);

  const initialItems = useMemo(() => ensureHomeLink(navItems, copy.home), [copy.home, navItems]);
  const [items, setItems] = useState<SiteNavItem[]>(initialItems);
  const [activeHref, setActiveHref] = useState(initialItems[0]?.href ?? "#top");
  const [open, setOpen] = useState(false);

  useEffect(() => {
    setItems(filterAvailableHashLinks(normalizeNavItems(navItems, copy.home)));
  }, [copy.home, navItems]);

  useEffect(() => {
    if (!items.length) return;

    if (!items.some((item) => item.href === activeHref)) {
      setActiveHref(items[0].href);
    }
  }, [activeHref, items]);

  useEffect(() => {
    if (typeof window === "undefined") return;

    const currentHash = window.location.hash;
    if (currentHash) {
      setActiveHref(currentHash);
    }
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return;

    const hashItems = items.filter((item) => item.href.startsWith("#"));
    const sections = hashItems
      .map((item) => {
        const id = item.href.slice(1);
        return document.getElementById(id);
      })
      .filter((section): section is HTMLElement => section instanceof HTMLElement);

    if (!sections.length) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const visibleEntries = entries
          .filter((entry) => entry.isIntersecting)
          .sort((entryA, entryB) => entryB.intersectionRatio - entryA.intersectionRatio);

        const nextActive = visibleEntries[0]?.target.id;
        if (nextActive) {
          setActiveHref(`#${nextActive}`);
        }
      },
      {
        rootMargin: "-18% 0px -52% 0px",
        threshold: [0.2, 0.4, 0.65],
      }
    );

    sections.forEach((section) => observer.observe(section));

    const onScroll = () => {
      if (window.scrollY < 48) {
        setActiveHref("#top");
      }
    };

    window.addEventListener("scroll", onScroll, { passive: true });

    return () => {
      observer.disconnect();
      window.removeEventListener("scroll", onScroll);
    };
  }, [items]);

  const handleSelect = useCallback(
    (event: React.MouseEvent<HTMLAnchorElement>, href: string) => {
      if (!href.startsWith("#")) {
        setOpen(false);
        return;
      }

      event.preventDefault();
      setActiveHref(href);
      setOpen(false);

      window.requestAnimationFrame(() => {
        scrollToHref(href, Boolean(prefersReducedMotion));
      });
    },
    [prefersReducedMotion]
  );

  const activeItem = items.find((item) => item.href === activeHref) ?? items[0];

  return (
    <>
      <header
        className={clsx(styles.header, transparent && styles.transparent)}
        style={sticky ? undefined : { position: "absolute" }}
      >
        <div className={styles.inner}>
          <a
            href="#top"
            className={styles.brand}
            onClick={(event) => handleSelect(event, "#top")}
          >
            <span className={styles.brandLabel}>{brandLabel}</span>
          </a>

          <div className={styles.actions}>
            <LanguageSwitcher
              current={locale}
              ariaLabel={copy.languageSwitcher}
              className={styles.localeSwitcher}
              linkClassName={styles.localeLink}
              activeClassName={styles.localeLinkActive}
            />

            <button
              ref={toggleRef}
              type="button"
              className={clsx(styles.toggle, open && styles.toggleOpen)}
              aria-expanded={open}
              aria-controls="site-nav-overlay"
              aria-label={open ? copy.close : copy.menu}
              onClick={() => setOpen((value) => !value)}
            >
              <span className={styles.toggleLabel}>{copy.menu}</span>
              <span className={styles.toggleIcon} aria-hidden="true">
                <span className={clsx(styles.toggleLine, styles.toggleLineTop)} />
                <span className={clsx(styles.toggleLine, styles.toggleLineBottom)} />
              </span>
            </button>
          </div>
        </div>
      </header>

      <div id="site-nav-overlay">
        <NavOverlay
          open={open}
          items={items}
          activeHref={activeHref}
          cta={cta}
          eyebrow={copy.overlayEyebrow}
          closeLabel={copy.close}
          ariaLabel={copy.overlayAria}
          navLabel={copy.navLabel}
          returnFocusRef={toggleRef}
          onClose={() => setOpen(false)}
          onSelect={handleSelect}
        />
      </div>
    </>
  );
}
